import { useMutation, useQuery } from '@apollo/client/react';
import {
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Input,
  Drawer,
  Textarea,
  Box,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';

import { toaster } from '#src/components/ui/toaster';
import { UPSERT_PROCESSOR } from '#src/graphql/mutations/upsert-processor';
import { GET_PROCESSORS } from '#src/graphql/queries/processors';

interface UpsertProcessorDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Optional processor ID to edit. If not provided, creates a new processor */
  processorId?: string;
  /** Callback when the drawer is closed */
  onClose?: () => void;
}

export function UpsertProcessorDrawer({
  open,
  processorId,
  onClose,
}: UpsertProcessorDrawerProps) {
  const [processorName, setProcessorName] = useState('');
  const [processorCode, setProcessorCode] = useState('');

  const { data: processorsData } = useQuery(GET_PROCESSORS);
  const [upsertProcessor, { loading: isSaving }] =
    useMutation(UPSERT_PROCESSOR);

  const editingProcessor = processorId
    ? processorsData?.processors?.processors?.find((p) => p.id === processorId)
    : null;

  useEffect(() => {
    if (editingProcessor) {
      setProcessorName(editingProcessor.id);
      setProcessorCode(editingProcessor.code);
    } else {
      setProcessorName('');
      setProcessorCode('');
    }
  }, [editingProcessor]);

  const handleSave = async () => {
    if (!processorName.trim() || !processorCode.trim()) {
      toaster.create({
        title: 'Validation Error',
        description: 'Please fill in both processor ID and code fields',
        type: 'error',
        duration: 5000,
        closable: true,
      });
      return;
    }

    try {
      await upsertProcessor({
        variables: {
          input: {
            id: processorName.trim(),
            code: processorCode.trim(),
          },
        },
      });

      toaster.create({
        title: 'Success',
        description: `Processor '${processorName}' ${editingProcessor ? 'updated' : 'created'} successfully!`,
        type: 'success',
        duration: 5000,
        closable: true,
      });

      handleClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Network error';
      toaster.create({
        title: 'Error',
        description: `Error saving processor: ${errorMessage}`,
        type: 'error',
        duration: 7000,
        closable: true,
      });
    }
  };

  const handleClose = () => {
    setProcessorName('');
    setProcessorCode('');
    onClose?.();
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      placement="end"
      size="lg"
    >
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content>
          <Drawer.Header>
            <Heading size="md" color="blue.500">
              {editingProcessor ? 'Edit Processor' : 'Create New Processor'}
            </Heading>
          </Drawer.Header>
          <Drawer.Body display="flex" flexDirection="column">
            <VStack gap={4} align="stretch" flex={1}>
              <Box>
                <Text fontSize="sm" color="fg.muted" mb={1}>
                  Processor ID (machine-readable name)
                </Text>
                <Input
                  value={processorName}
                  onChange={(e) => setProcessorName(e.target.value)}
                  placeholder="e.g., my-transform"
                  readOnly={!!editingProcessor}
                  bg={editingProcessor ? 'bg.subtle' : 'bg'}
                />
              </Box>
              <Box flex={1} display="flex" flexDirection="column">
                <Text fontSize="sm" color="fg.muted" mb={1}>
                  Code (JavaScript that exports a function process(variables))
                </Text>
                <Textarea
                  value={processorCode}
                  onChange={(e) => setProcessorCode(e.target.value)}
                  placeholder={
                    'e.g., module.exports = { process: function(variables) { return variables; } };'
                  }
                  flex={1}
                  resize="vertical"
                  fontFamily="mono"
                  fontSize="sm"
                />
              </Box>
            </VStack>
          </Drawer.Body>
          <Drawer.Footer>
            <HStack gap={2}>
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                colorScheme="blue"
                loading={isSaving}
                disabled={!processorName.trim() || !processorCode.trim()}
              >
                {isSaving
                  ? 'Saving...'
                  : editingProcessor
                    ? 'Update Processor'
                    : 'Create Processor'}
              </Button>
            </HStack>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
